/**
 * upGrad internship enrolment + payment API.
 *
 * Split from `internshipData` (the catalogue) because this is a different
 * concern with different failure handling: the catalogue swallows errors and
 * degrades to an empty list, whereas a failed enrolment MUST surface its message
 * to the student. Both talk to the same host, so the base URL resolver is shared
 * rather than duplicated.
 *
 * Flow: the Apply Now form POSTs the student's details here, the service answers
 * with a one-time Atom Paynetz grant, the browser hands that to the checkout SDK
 * (see `hooks/useAtomPayment`), and Atom redirects back to /internship with a
 * `?transactionId=` that is looked up here.
 *
 * Endpoints:
 *   POST /api/users/enroll
 *   GET  /api/transactions/:transactionId/status
 */

import {
  resolveInternshipApiBaseUrl,
  type IInternshipCourse,
} from "./internshipData";

/** Names this module in the console when the base URL is missing. */
const CONTEXT = "internshipPayment";

/** Payload for `POST /users/enroll` — mirrors the source's `IEnrollForm`. */
export interface IInternshipEnrolment {
  fullName: string;
  email: string;
  /** ISO 8601 string; the form holds a Date and converts on submit. */
  dob: string;
  mobileNumber: string;
  fatherName: string;
  motherName: string;
  universityName: string;
  collegeName: string;
  programName: string;
  studyYear: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  abcId: string;
  aadhaarId: string;
  courseId: string;
}

/** What the service returns on a successful enrolment — an Atom checkout grant. */
export interface IInternshipEnrolmentResponse {
  token?: string;
  merchantID?: string;
  returnUrl?: string;
  redirectionUrl?: string;
  email?: string;
  mobileNumber?: string;
}

export interface IInternshipTransaction {
  transactionId?: string;
  /** "SUCCESS" | "FAILED" | "PENDING" as sent by the service. */
  paymentStatus?: string;
  amount?: string;
  paidAt?: string;
  courseDetails?: IInternshipCourse;
}

/**
 * Unlike the catalogue reads, a failed enrolment must surface its message — the
 * form shows it inline — so this returns a result object instead of swallowing
 * the error.
 */
export type IInternshipEnrolmentResult =
  | { ok: true; data: IInternshipEnrolmentResponse }
  | { ok: false; message: string };

const GENERIC_ENROLMENT_ERROR = "Something went wrong";

/**
 * The service is NestJS + class-validator, so a rejected enrolment answers
 * `{ message, error, statusCode }` where `message` is an ARRAY of per-field
 * strings ("email must be an email", …) — one string only for non-validation
 * failures. Verified against the dev host.
 *
 * Both shapes are folded into one sentence. Without this an array falls through
 * to the generic message and the student is told "Something went wrong" while
 * the service is naming the exact field it rejected.
 */
const readEnrolmentError = (body: unknown): string => {
  const message = (body as { message?: unknown } | null)?.message;

  if (typeof message === "string" && message.trim()) return message.trim();

  if (Array.isArray(message)) {
    const lines = message
      .filter((line): line is string => typeof line === "string" && !!line.trim())
      .map((line) => line.trim());
    if (lines.length) return lines.join(". ") + ".";
  }

  return GENERIC_ENROLMENT_ERROR;
};

export async function enrolInternshipUser(
  payload: IInternshipEnrolment,
  signal?: AbortSignal
): Promise<IInternshipEnrolmentResult> {
  const baseUrl = resolveInternshipApiBaseUrl(CONTEXT);
  if (!baseUrl) return { ok: false, message: GENERIC_ENROLMENT_ERROR };

  try {
    const res = await fetch(`${baseUrl}/api/users/enroll`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify(payload),
      signal,
    });

    // Read the body either way — failures carry the message worth showing.
    const body = await res.json().catch(() => null);

    if (!res.ok) {
      return { ok: false, message: readEnrolmentError(body) };
    }

    return { ok: true, data: (body ?? {}) as IInternshipEnrolmentResponse };
  } catch {
    return { ok: false, message: GENERIC_ENROLMENT_ERROR };
  }
}

/**
 * Looks up a transaction after Atom redirects back. Returns null when the
 * lookup fails; the caller treats that as a failed payment rather than dropping
 * the student into an empty payment form, matching the source.
 */
export async function getInternshipTransaction(
  transactionId: string,
  signal?: AbortSignal
): Promise<IInternshipTransaction | null> {
  const baseUrl = resolveInternshipApiBaseUrl(CONTEXT);
  if (!baseUrl) return null;

  try {
    const res = await fetch(
      `${baseUrl}/api/transactions/${encodeURIComponent(transactionId)}/status`,
      { headers: { accept: "application/json" }, signal }
    );
    if (!res.ok) return null;
    return (await res.json()) as IInternshipTransaction;
  } catch {
    return null;
  }
}
